import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Form, ListGroup, Row } from 'react-bootstrap';
import { Controller, useForm, type FieldValues } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import CircularCheckIcon from '../../assets/Icon/CircularCheckIcon';
import AuthContext from '../../contexts/AuthContext';
import TokenListContext from '../../contexts/TokenListContext';
import useAPI from '../../hooks/useAPI';
import TokenTransferConfirmationModal from '../dialogs/token-transfer-confirmation-dialog';
import TransactionSuccessModal from '../dialogs/transaction-success-dialog';
import FormError from '../ui/FormError';
import Input from '../ui/Input';
import styles from './style.module.scss';
interface Token {
  tokenId: string;
  tokenAmount: string;
  transactionDate: string;
  transactionId: string;
  status: string;
}

interface UserItem {
  id: number;
  name: string;
  email: string;
}

export default function TransferTokens({
  setActiveTab,
  activeTab,
}: {
  setActiveTab: (tab: string) => void;
  activeTab: string;
}) {
  const navigate = useNavigate();
  const { adminBalance } = useContext(AuthContext);
  const [amountValue, setAmountValue] = useState('');

  const schema = z.object({
    amount: z
      .string()
      .min(1, 'Total tokens must be greater than 0')
      .refine(val => !isNaN(Number(val)) && Number(val) > 0, {
        message: 'Total tokens must be a positive number greater than 0',
      })
      .refine(val => Number(val) <= (adminBalance?.totalBalance ?? 0), {
        message: 'Requested amount exceeds available tokens',
      }),
    receiverId: z.string().refine(val => val !== '', {
      message: 'Please select a receiver',
    }),
  });

  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [transactionId, setTransactionId] = useState<string | null>();
  const [userList, setUserList] = useState<UserItem[]>([]);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Search states
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { tokenList } = useContext(TokenListContext);

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const addRemoveItem = (item: string) => {
    setSelectedUser(item);

    setValue('receiverId', item);
  };

  const { refetch: transferToken, isLoading } = useAPI({
    url: '/token/transfer',
    method: 'POST',
    onSuccess: data => {
      setOpen(false);
      setSelectedUser(null);
      setOpenSuccessModal(true);
      setActiveTab('transfer-history');
      setTransactionId(data.result.data.transactionId);
    },
    onError: error => {
      error?.response?.data?.message &&
        toast.error(error?.response?.data?.message);
    },
  });

  const { refetch: fetchUsers, isLoading: isUsersLoading } = useAPI({
    url: '/auth/admin/user/list',
    method: 'GET',
    autoFetch: false,
    onSuccess: data => {
      const fetched: UserItem[] =
        data?.result?.users?.map((item: any) => ({
          id: item?.id,
          name:
            item?.firstName || item?.lastName
              ? `${item?.firstName?.trim()} ${item?.lastName?.trim()}`.trim()
              : item?.email,
          email: item?.email,
        })) || [];

      setUserList((prev: UserItem[]) => {
        const existingIds = new Set(prev.map((u: UserItem) => u.id));
        const deduped = fetched.filter((u: UserItem) => !existingIds.has(u.id));
        return [...prev, ...deduped];
      });

      setHasMore((fetched?.length || 0) === PAGE_SIZE);
    },
  });

  // initial load
  useEffect(() => {
    fetchUsers({ page: 1, limit: PAGE_SIZE, search: searchQuery } as any);
  }, []);

  // Search debounce effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        setUserList([]);
        setPage(1);
        setHasMore(true);
        fetchUsers({ page: 1, limit: PAGE_SIZE, search: searchInput } as any);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // infinite scroll observer
  useEffect(() => {
    if (!hasMore) return;
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      entries => {
        const first = entries[0];
        if (first.isIntersecting && !isUsersLoading && hasMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchUsers({
            page: nextPage,
            limit: PAGE_SIZE,
            search: searchQuery,
          } as any);
        }
      },
      { root: containerRef.current || null },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [page, isUsersLoading, hasMore, searchQuery]);

  const [amount, receiverId] = watch(['amount', 'receiverId']);

  const onConfirm = () => {
    if (tokenList[0]?.tokenId) {
      const payload = {
        amount: amount,
        tokenId: tokenList[0]?.tokenId,
        receiverId: receiverId,
      };
      if (!isLoading) transferToken(payload);
    } else {
      toast.error(
        `You haven't created any tokens in the Token Management module. Please generate the required tokens before approving user requests.`,
      );
    }
  };

  useEffect(() => {
    reset({});
    setAmountValue('');
    // reset list state when tab changes
    setUserList([]);
    setPage(1);
    setHasMore(true);
    setSearchInput('');
    setSearchQuery('');
    fetchUsers({ page: 1, limit: PAGE_SIZE, search: '' } as any);
  }, [activeTab]);

  const handleTransfer = (data: FieldValues) => {
    setOpen(true);
  };

  const tokenNameOrSymbol = useMemo(
    () => `${tokenList?.[0]?.tokenName}(${tokenList?.[0]?.tokenSymbol})`,
    [tokenList],
  );

  return (
    <>
      <div className={styles.transferTokenForm} ref={containerRef}>
        <Row className="g-0">
          <Col
            xs={12}
            md={6}
            lg={7}
            style={{ overflowY: 'auto', maxHeight: '60vh' }}
          >
            <div className="px-3 py-4 h-100">
              <div className="mb-3 fw-semibold">
                <p className="mb-0">Member</p>
              </div>
              {/* Search Input */}
              <div className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Search by name or email"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="rounded-2"
                  size="sm"
                />
              </div>
              <ListGroup as="ol" className="mb-0">
                {userList.length > 0 ? (
                  userList.map(
                    (item: { id: number; name: string; email: string }) => (
                      <ListGroup.Item
                        className="d-flex py-3 px-3 justify-content-between align-items-center gap-2"
                        key={item.id}
                        onClick={() => addRemoveItem(item.id.toString())}
                        style={{
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = '';
                        }}
                      >
                        <div className="d-flex flex-column flex-grow-1">
                          {item.name && item.name !== item.email && (
                            <span className="fw-semibold">{item.name}</span>
                          )}
                          <span className="text-muted small">{item.email}</span>
                        </div>
                        <CircularCheckIcon
                          color={
                            selectedUser !== item.id.toString()
                              ? '#999999'
                              : 'var(--primary)'
                          }
                        />
                      </ListGroup.Item>
                    ),
                  )
                ) : (
                  <div className="text-center py-4 text-muted">
                    {isUsersLoading ? 'Loading members...' : 'No members found'}
                  </div>
                )}
              </ListGroup>
              <div ref={loadMoreRef as any} style={{ height: 1 }} />
              {errors.receiverId?.message && (
                <p className="text-danger mt-2 mb-0">
                  {errors.receiverId.message as string}
                </p>
              )}
            </div>
          </Col>
          <Col xs={12} md={6} lg={5} className={styles.amountColumn}>
            <div className="px-2 px-md-3 py-3 py-md-4 h-100">
              <p className="mb-3 fw-semibold">Amount</p>
              <form>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="amount">
                    Total Amount <span className="text-danger">*</span>
                  </Form.Label>
                  <Controller
                    name="amount"
                    control={control}
                    render={({ field }) => (
                      <>
                        <div style={{ position: 'relative' }}>
                          <NumericFormat
                            thousandSeparator
                            decimalScale={0}
                            allowNegative={false}
                            customInput={Input}
                            value={amountValue}
                            onValueChange={value => {
                              field.onChange(value.value);
                              setAmountValue(value.value);
                            }}
                            placeholder="Enter amount"
                            style={{
                              paddingRight: tokenList?.[0]?.tokenSymbol
                                ? '60px'
                                : '10px',
                            }}
                          />
                          {tokenList?.[0]?.tokenSymbol && (
                            <span
                              style={{
                                position: 'absolute',
                                right: '15px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#666',
                                fontWeight: 600,
                                pointerEvents: 'none',
                              }}
                            >
                              {tokenList[0].tokenSymbol}
                            </span>
                          )}
                        </div>
                        {errors.amount?.message && (
                          <FormError
                            message={errors.amount?.message as string}
                          />
                        )}
                      </>
                    )}
                  />
                </Form.Group>
              </form>
            </div>
          </Col>
        </Row>
      </div>
      <div className="d-flex my-3 gap-2 justify-content-end align-items-center">
        <Button
          size="lg"
          onClick={() => navigate('/token-management')}
          variant="outline-secondary border-2 rounded-2 text-dark bg-white fw-bold"
        >
          Back
        </Button>
        <Button
          size="lg"
          variant="filled"
          className="btn-dark"
          disabled={isLoading}
          onClick={handleSubmit(handleTransfer)}
        >
          Confirm Transfer
        </Button>
        <TokenTransferConfirmationModal
          open={open}
          onReject={() => setOpen(false)}
          isLoading={isLoading}
          onConfirm={onConfirm}
          information={{
            tokenOrSymbol: tokenNameOrSymbol,
            TotalTransferAmount: Number(amount),
            IndividualTransferAmount: Number(amount),
            // DistritionSchedule: 'Immediate',
          }}
        />
        <TransactionSuccessModal
          open={openSuccessModal}
          title="Tokens transferred successfully"
          isCloseIconVisible={true}
          onClose={() => setOpenSuccessModal(false)}
        />
      </div>
    </>
  );
}
